// Pure logic for the Content panel (custom content types + entries) and the
// Custom fields cards. No React/store imports so it stays unit-testable.

import type { AdminTarget, ApiMetaobject, FieldDefinition, FieldType, MetafieldOwner } from '@dunea/plugin-sdk';
import type { DataDefinition } from '~/lib/api/data';

export { apiErrorMessage } from '~/components/workbench/tools/payments/payments-model';

/** Studio's caps (apps/studio/src/modules/plugins/data/data.service.ts). */
export const MAX_FIELDS = 40;
export const PAGE_SIZE = 100;

const KEY_RE = /^[a-z][a-z0-9_]{0,40}$/;

/** "Team member" → "team_member" (a valid type handle / field key), '' when nothing is left. */
export function toKey(label: string): string {
  const key = label
    .normalize('NFKD')
    .replace(/\p{M}+/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^[^a-z]+/, '')
    .replace(/_+$/, '')
    .slice(0, 41)
    .replace(/_+$/, '');

  return KEY_RE.test(key) ? key : '';
}

/** The owner may change the type itself only in `custom` (plugin types belong to the plugin). */
export function canEditDefinition(def: Pick<DataDefinition, 'namespace'>): boolean {
  return def.namespace === 'custom';
}

/** Entries / values: in `custom`, or where the plugin left ownerWritable on. */
export function canEditValues(def: Pick<DataDefinition, 'namespace' | 'access'>): boolean {
  return def.namespace === 'custom' || def.access.ownerWritable;
}

/** Where a definition comes from, for a small caption. Null for the owner's own. */
export function namespaceLabel(ns: string): string | null {
  if (ns === 'custom') {
    return null;
  }

  const name = ns.startsWith('app--') ? ns.slice(5) : ns;

  return `From ${name.replace(/-/g, ' ')}`;
}

/** An entry's title: the display field, else the first text value, else its handle. */
export function entryTitle(
  entry: Pick<ApiMetaobject, 'fields' | 'handle'>,
  def?: Pick<DataDefinition, 'displayField' | 'fields'>,
): string {
  const pick = (key: string | undefined) => {
    const v = key ? entry.fields[key] : undefined;
    return typeof v === 'string' && v.trim() ? v.trim() : null;
  };

  const fromDisplay = pick(def?.displayField);

  if (fromDisplay) {
    return fromDisplay;
  }

  for (const f of def?.fields ?? []) {
    if (f.type === 'single_line_text' && !f.list) {
      const v = pick(f.key);

      if (v) {
        return v;
      }
    }
  }

  return entry.handle;
}

/** Move one item (reorder buttons). Out-of-range moves return the list unchanged. */
export function moveItem<T>(list: readonly T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) {
    return [...list];
  }

  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);

  return next;
}

// ─── type editor draft ───

export interface FieldDraft {
  /** Stable React key; not sent. */
  uid: string;
  key: string;
  label: string;
  type: FieldType;
  required: boolean;
  list: boolean;
  help: string;
  /** select: one option per line or comma ("Small, Medium, Large"). */
  optionsText: string;
  /** metaobject_reference: the type handle. */
  targetType: string;
  /** The key was typed by hand (stop deriving it from the label). */
  keyTouched: boolean;
}

export interface TypeDraft {
  name: string;
  type: string;
  typeTouched: boolean;
  description: string;
  displayField: string;
  isPublic: boolean;
  fields: FieldDraft[];
}

let uidSeq = 0;

export function newFieldDraft(over: Partial<FieldDraft> = {}): FieldDraft {
  uidSeq += 1;

  return {
    uid: `f${uidSeq}`,
    key: '',
    label: '',
    type: 'single_line_text',
    required: false,
    list: false,
    help: '',
    optionsText: '',
    targetType: '',
    keyTouched: false,
    ...over,
  };
}

export function emptyTypeDraft(): TypeDraft {
  return {
    name: '',
    type: '',
    typeTouched: false,
    description: '',
    displayField: '',
    isPublic: true,
    fields: [newFieldDraft({ label: 'Title', key: 'title', required: true })],
  };
}

/** Draft from an existing type (editing). */
export function typeDraftFrom(def: DataDefinition): TypeDraft {
  return {
    name: def.name,
    type: def.key,
    typeTouched: true,
    description: def.description ?? '',
    displayField: def.displayField ?? '',
    isPublic: def.access.storefront === 'public',
    fields: (def.fields ?? []).map((f) =>
      newFieldDraft({
        key: f.key,
        label: f.label,
        type: f.type,
        required: Boolean(f.required),
        list: Boolean(f.list),
        help: f.help ?? '',
        optionsText: (f.options ?? []).map((o) => (o.label === o.value ? o.value : `${o.label}=${o.value}`)).join('\n'),
        targetType: f.validations?.metaobjectType ?? '',
        keyTouched: true,
      }),
    ),
  };
}

/** "Small\nMedium" or "S, M" (or "Label=value") → select options. */
export function parseOptions(text: string): { value: string; label: string }[] {
  const seen = new Set<string>();
  const out: { value: string; label: string }[] = [];

  for (const raw of text.split(/[\n,]/)) {
    const part = raw.trim();

    if (!part) {
      continue;
    }

    const eq = part.indexOf('=');
    const label = (eq > 0 ? part.slice(0, eq) : part).trim().slice(0, 80);
    const value = (eq > 0 ? part.slice(eq + 1) : part).trim().slice(0, 80);

    if (value && !seen.has(value)) {
      seen.add(value);
      out.push({ value, label: label || value });
    }
  }

  return out;
}

/** The draft as FieldDefinitions (what studio receives). */
export function draftFields(draft: TypeDraft): FieldDefinition[] {
  return draft.fields.map((f) => {
    const def: FieldDefinition = { key: f.key, label: f.label.trim(), type: f.type };

    if (f.required) {
      def.required = true;
    }

    if (f.list) {
      def.list = true;
    }

    if (f.help.trim()) {
      def.help = f.help.trim();
    }

    if (f.type === 'select') {
      def.options = parseOptions(f.optionsText);
    }

    if (f.type === 'metaobject_reference' && f.targetType) {
      def.validations = { metaobjectType: f.targetType };
    }

    return def;
  });
}

/** First problem with a draft, in the order the owner would fix them; null when it can be saved. */
export function typeDraftProblem(draft: TypeDraft): string | null {
  if (!draft.name.trim()) {
    return 'Give the content type a name.';
  }

  if (!KEY_RE.test(draft.type)) {
    return 'The handle must start with a letter and use lowercase letters, digits and _.';
  }

  if (!draft.fields.length) {
    return 'Add at least one field.';
  }

  if (draft.fields.length > MAX_FIELDS) {
    return `A content type can have at most ${MAX_FIELDS} fields.`;
  }

  const keys = new Set<string>();

  for (const [i, f] of draft.fields.entries()) {
    const name = f.label.trim() || `Field ${i + 1}`;

    if (!f.label.trim()) {
      return `Field ${i + 1} needs a label.`;
    }

    if (!KEY_RE.test(f.key)) {
      return `"${name}" needs a key of lowercase letters, digits and _.`;
    }

    if (keys.has(f.key)) {
      return `Two fields use the key "${f.key}".`;
    }

    keys.add(f.key);

    if (f.type === 'select' && !parseOptions(f.optionsText).length) {
      return `"${name}" needs at least one option.`;
    }

    if (f.type === 'metaobject_reference' && !f.targetType) {
      return `Choose which content type "${name}" points to.`;
    }
  }

  if (draft.displayField && !keys.has(draft.displayField)) {
    return 'The title field is not one of the fields.';
  }

  return null;
}

// ─── custom fields cards ───

/** Admin targets that show the Custom fields card, and the resource they edit. */
export const CUSTOM_FIELD_TARGETS: Partial<Record<AdminTarget, MetafieldOwner>> = {
  'shop.product.details': 'product',
  'shop.order.details': 'order',
  'shop.collection.details': 'collection',
  'funnels.funnel.details': 'funnel',
};

/** Resource types the owner can add custom fields to from the Content panel. */
export const FIELD_OWNERS: readonly (readonly [MetafieldOwner, string])[] = [
  ['product', 'Products'],
  ['variant', 'Variants'],
  ['collection', 'Collections'],
  ['order', 'Orders'],
  ['page', 'Pages'],
  ['funnel', 'Funnels'],
  ['app', 'Whole site'],
];

export function ownerLabel(owner: string): string {
  return FIELD_OWNERS.find(([o]) => o === owner)?.[1] ?? owner;
}

/** Form key for a metafield definition, unique across namespaces. */
export function metafieldFormKey(def: Pick<DataDefinition, 'namespace' | 'key'>): string {
  return `${def.namespace}__${def.key}`;
}

/** Changed values between the saved and the edited form, as metafield writes (null = remove). */
export function changedMetafields(
  defs: readonly Pick<DataDefinition, 'namespace' | 'key'>[],
  saved: Record<string, unknown>,
  edited: Record<string, unknown>,
): { namespace: string; key: string; value: unknown }[] {
  const out: { namespace: string; key: string; value: unknown }[] = [];
  const norm = (v: unknown) =>
    v === undefined || v === null || v === '' || (Array.isArray(v) && !v.length) ? null : v;

  for (const def of defs) {
    const k = metafieldFormKey(def);
    const before = norm(saved[k]);
    const after = norm(edited[k]);

    if (JSON.stringify(before) !== JSON.stringify(after)) {
      out.push({ namespace: def.namespace, key: def.key, value: after });
    }
  }

  return out;
}
